import {UserId} from 'records/User';
import {List, Map} from 'immutable';
import {AxisState, MetricParams} from '@store/@types/metric';
import {KpiTarget} from '@store/@types/kpi';
import {getAxisValue} from '@store/metrics-trends/metrics-trends.utils';
import {IdMap} from '@store/@types/util';
import {Moment} from 'moment';
import CoachingPlan, {PlanId} from 'records/CoachingPlan';
import {CoachingPlanVelocity} from 'records/CoachingPlanVelocity';
import {sumBy} from 'utils/sum';
import isEmpty from 'lodash/isEmpty';
import {skillColorNineBox} from 'utils/skill-color-by-idx';
import {Property} from 'csstype';
import {
  getEarliest,
  getLatest,
  getRange,
} from 'components/dashboard/manager/nine-box/GraphRange.utils';
import {axisLabel, dropNulls} from 'components/dashboard/manager/nine-box/NineBox.utils';
import moment from 'moment';

export interface OneRepChartPoints {
  earliest: OneRepChartPoint;
  latest: OneRepChartPoint;
}
export interface OneRepChartPoint {
  date: Moment;
  x: number;
  y: number;
  xValueStr: string;
  yValueStr: string;

  // Used for extra data needed for point specific popups/tooltips
  extra?: any;
}

// Returns the latest and earliest points for both axes
export function repChartById(
  repId: UserId,
  horizontal: AxisState,
  vertical: AxisState,
  kpisById: IdMap<KpiTarget>,
  titleMap: Map<MetricParams, string>
): OneRepChartPoints | undefined {
  const horizontalData = dropNulls(horizontal, repId);
  const verticalData = dropNulls(vertical, repId);
  // Ensure we have at least one non-null value in both data sets.
  if (isEmpty(horizontalData) || isEmpty(verticalData)) return;
  const range = getRange(horizontal, vertical, kpisById);
  if (!range) return;

  const earlyX = getEarliest(horizontalData);
  const earlyY = getEarliest(verticalData);
  const lateX = getLatest(horizontalData);
  const lateY = getLatest(verticalData);
  const earliestDate = moment.min([earlyX.date, earlyY.date, lateX.date, lateY.date]);
  const latestDate = moment.max([earlyX.date, earlyY.date, lateX.date, lateY.date]);

  const earliest = {
    date: earliestDate,
    x: getAxisValue(range.xRange, earlyX.value),
    y: getAxisValue(range.yRange, earlyY.value),
    xValueStr: axisLabel(titleMap, horizontal.metric, kpisById, earlyX.value),
    yValueStr: axisLabel(titleMap, vertical.metric, kpisById, earlyY.value),
  };
  const latest = {
    date: latestDate,
    x: getAxisValue(range.xRange, lateX.value),
    y: getAxisValue(range.yRange, lateY.value),
    xValueStr: axisLabel(titleMap, horizontal.metric, kpisById, lateX.value),
    yValueStr: axisLabel(titleMap, vertical.metric, kpisById, lateY.value),
  };

  return {latest, earliest};
}

export interface PlanSkillDiff {
  id: PlanId;
  coachName: string;
  skillName: string;
  // Percentage number
  pctChange: string;
  color: Property.Color;
}

const pctFormatter = new Intl.NumberFormat('en-EN', {
  style: 'percent',
  maximumFractionDigits: 1,
});
export function repSkillChanges(
  repPlans: List<CoachingPlan>,
  velocityMap: Map<PlanId, CoachingPlanVelocity>,
  timeSpentByPlan: Map<PlanId, number>
): List<PlanSkillDiff> {
  return repPlans
    .map((plan, index) => {
      const change = velocityMap.get(plan.id)?.changeInScore;
      return {
        id: plan.id,
        coachName: plan.coach.info!.getDisplayName(),
        skillName: plan.getSkillNames(),
        // we filter out undefined changeInScore in NineBoxContainer
        pctChange: pctFormatter.format(change! / 100),
        color: skillColorNineBox(index),
      };
    })
    .sortBy(diff => timeSpentByPlan.get(diff.id));
}

interface CoachingRange {
  plans: List<CoachingPlan>;
  segment: [Moment, Moment];
}

export interface LineData {
  size: number;
  plan?: CoachingPlan;
}

/**
 * Coaching plan line data is generated in two passes.
 *
 * First we check for any periods where there is no active coaching plan.
 *   These segments will be represented by grey lines, and take up as much space
 *   as is proportional to the given date range.
 *   i.e. a range of 1 month with 1 week of no coaching would have a grey line
 *   taking up 1/4th of the entire line.
 *
 * The remaining segments of the line are split up between the active plans
 *   based on how much coaching took place in each plan. With the plan sub-segments
 *   being ordered based on their start dates.
 *
 */
export function coachingLineData(
  start: Moment,
  end: Moment,
  repPlans: List<CoachingPlan>,
  planCoachingTime: Map<PlanId, number>
): List<LineData> {
  // If we have no plans, return a single no-plan line
  if (repPlans.isEmpty()) {
    return List([{size: 1}]);
  }

  // Assumes plans are sorted by created date
  const ranges = coachingPlanRanges(start, end, repPlans);
  const segments = equalizeLineSegments(ranges, planCoachingTime);
  if (sumBy(segments, s => s.size) > 1) {
    console.warn('Line segments total over 1', {
      ranges,
      segments,
      start,
      end,
      repPlans,
      planCoachingTime,
      coachingPlanRanges,
      equalizeLineSegments,
    });
  }
  return segments;
}

// Returns a list of ranges in order from start to end
function coachingPlanRanges(
  start: Moment,
  end: Moment,
  repPlans: List<CoachingPlan>
): List<CoachingRange> {
  let prevRange: CoachingRange = {plans: List(), segment: [start, start]};
  const ranges: CoachingRange[] = [prevRange];

  // Extends the previously set range with the given end date
  function extendRange(plan: CoachingPlan, newEnd: Moment) {
    const {
      plans,
      segment: [prevStart],
    } = prevRange;
    prevRange = {plans: plans.push(plan), segment: [prevStart, newEnd]};
    ranges.pop();
    return ranges.push(prevRange);
  }

  // Add an empty or nonEmpty range
  function addRange(start: Moment, end: Moment, plan?: CoachingPlan) {
    const range: CoachingRange = {
      plans: plan ? List([plan]) : List(),
      segment: [start, end],
    };
    if (plan) {
      // Don't set prevRange with empty ranges
      prevRange = range;
    }
    return ranges.push(range);
  }

  let incompletePlan = false;
  // Iterate over all of our plans
  for (const plan of repPlans) {
    const [, prevEnd] = prevRange.segment;

    // If we've hit an incomplete plan, then extend that with all the following plans
    if (incompletePlan) {
      extendRange(plan, end);
      continue;
    }

    if (plan.created.isBefore(prevEnd)) {
      // incomplete plans will overrun the remaining line
      if (!plan.completedAt) {
        extendRange(plan, end);
        incompletePlan = true;
        continue;
      }

      // this plan overlaps with our previous range, so we extend it
      extendRange(plan, plan.completedAt);
    } else {
      // this plan introduces a gap between the last range and itself
      // so add an empty range to represent that gap
      addRange(prevEnd, plan.created);

      // and add the range for the plan itself
      if (!plan.completedAt) {
        // incomplete plans will overrun the remaining line
        addRange(plan.created, end, plan);
        break;
      } else {
        addRange(plan.created, plan.completedAt, plan);
      }
    }
  }

  // In the event our latest plan ends before our end date, make sure we add a final empty range.
  const latestEnd = repPlans.sortBy(p => p.completedAt).first();
  if (latestEnd.completedAt?.isBefore(end)) {
    const prevEnd = prevRange.segment[1];
    addRange(prevEnd, end);
  }

  return List(ranges);
}

// Same unit that review time is saved in
const DIFF_UNIT = 'seconds';

// Given ranges for our line segments divide up the space between the plans based on their coaching time
function equalizeLineSegments(
  ranges: List<CoachingRange>,
  timeSpentMap: Map<PlanId, number>
): List<LineData> {
  const rangeStart = ranges.first().segment[0];
  const diff = (date: Moment) => date.diff(rangeStart, DIFF_UNIT);

  // convert ranges to numerical
  const numericRanges = ranges.map(r => ({...r, segment: r.segment.map(diff)}));
  const totalSize = diff(ranges.last().segment[1]); // diff between start and end
  if (totalSize === 0) return List();

  return numericRanges.flatMap(range => {
    // get size of this range and bail if 0 (can't divide by 0, or render a 0 length line)
    const [start, end] = range.segment;
    const rangeDiff = end - start;
    if (rangeDiff === 0) return List();

    // empty periods don't need to be split up
    if (range.plans.isEmpty()) {
      return List([{size: rangeDiff / totalSize}]);
    }
    // Segment length is proportional to the plan's coaching time vs other plans in its segment
    const planTimeTotal = sumBy(range.plans, p => timeSpentMap.get(p.id, 0));
    if (planTimeTotal === 0) return List([{size: rangeDiff / totalSize}]);

    return range.plans.map(plan => {
      // a ratio of how much this plan coached vs the others
      const pctTimeCoached = timeSpentMap.get(plan.id, 0) / planTimeTotal;
      // if there was no coaching, don't include
      if (pctTimeCoached === 0) {
        return List();
      }

      const size = (rangeDiff * pctTimeCoached) / totalSize;
      return {plan, size};
    });
  });
}
